import React, {useState, useEffect} from 'react';
import {GoogleMap, LoadScript, Polygon, Circle, Marker, useJsApiLoader, useLoadScript} from '@react-google-maps/api';
import {Image, Table, message, Row, Col, Input, Button, Tooltip} from 'antd';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import MapStyle from "./MapStyleComponent";
import config from '../business/config.json';
import {useAxiosExternalAPI} from "../hooks/hooks";

// var axios_google = axios.create({});
// delete axios_google.defaults.headers.common.Authorization;

const GOOGLE_MAPS_JS_API_KEY = config.googlemaps_api_key;

const GOOGLE_MAPS_GEOCODING_URL =
    "https://maps.googleapis.com/maps/api/geocode/json?" +
    "key=" + GOOGLE_MAPS_JS_API_KEY +
    config.googlemaps_api_query +
    "&address="
;
const DEFAULT_POLYGON_PARSED =
    [{lng: 151.1164522, lat: -33.9541245}, {lng: 151.1165032, lat: -33.9541912}, {
        lng: 151.1166426,
        lat: -33.9541111
    }, {lng: 151.1165917, lat: -33.9540477}]
;

const DEFAULT_POLYGON = JSON.stringify(DEFAULT_POLYGON_PARSED);
const MAX_ZOOM = 30;

const DEFAULT_VALUE = {
    latitude: -33.9540167,
    longitude: 151.1167833,
    polygon: DEFAULT_POLYGON,
    address: "",
    suburb: "",
    city: "",
    region: "",
    postcode: "",
    map_zoom: MAX_ZOOM
};

const DEFAULT_LAT_LNG = {
    lat: -33.9540167,
    lng: 151.1167833
};

function PolygonMapComponent(props) {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const axios_google = useAxiosExternalAPI('', GOOGLE_MAPS_JS_API_KEY)
    // delete axios_google.defaults.headers.common.Authorization;

    const {t} = useTranslation();
    const [map, setMap] = useState(null);
    const [staticMapURL, setStaticMapURL] = useState(null);
    const [latLng, setLatLng] = useState(DEFAULT_LAT_LNG);
    const [polygon, setPolygon] = useState(DEFAULT_POLYGON_PARSED);
    const [value, setValue] = useState(props.value || DEFAULT_VALUE);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_JS_API_KEY
    });
    const [containerStyle] = useState({
        width: '100%',
        height: '500px',
        position: 'relative'
    });
    const [mapStyle, setMapStyle] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [search, setSearch] = useState("");
    const [addresses, setAddresses] = useState([]);
    const goto_i18n = t("Go To Address");
    const setmarker_i18n = t("Set Marker");
    const columns = [
        {
            title: t("Address"),
            dataIndex: "formatted_address",
            key: "formatted_address"
        },
        {
            title: t("Go to"),
            key: "action_1",
            render: function (t, r, i) {
                return (
                    <Tooltip
                        title={goto_i18n}
                    >
                        <Button
                            icon={<FontAwesomeIcon icon="plane"/>}
                            onClick={(e) => goToAddress(i)}
                        />
                    </Tooltip>
                );
            }
        },
        {
            title: t("Set"),
            key: "action_2",
            render: function (t, r, i) {
                return (
                    <Tooltip
                        title={setmarker_i18n}
                    >
                        <Button
                            type="primary"
                            icon={<FontAwesomeIcon icon="map-marker-alt"/>}
                            onClick={(e) => setAddress(i)}
                        />
                    </Tooltip>
                );
            }
        }
    ];

    const updateZoomValue = function () {
        const v = JSON.parse(JSON.stringify(value));
        if (typeof map == "undefined" || map == null) {
            return;
        }
        var zoom = map.getZoom();
        if (zoom > MAX_ZOOM) {
            zoom = MAX_ZOOM;
            map.setZoom(zoom);
            return;
        }
        v.map_zoom = map.getZoom();
        setValue(v);
    }

    const updateAddress = function (e) {
        setSearch(e.target.value);
    }

    const searchAddress = function () {
        const url = GOOGLE_MAPS_GEOCODING_URL + search;
        console.log(url)
        axios_google.current.get(
            url
        ).then(
            function (response) {
                const data = response.data;
                switch (data.status) {
                    case "ZERO_RESULTS":
                        message.info(t("No addresses found."));
                        break;
                    case "OK":
                        message.info(t("Addresses found.", {count: data.results.length}));
                        break;
                    default:
                    //nothing
                }
                data.results.forEach(function (d, i) {
                    data.results[i].key = i;
                });
                setAddresses(data.results);
            }
        ).catch(function (e) {
            console.log(e)
        }).then(function () {
            //Nothing
            console.log("error")
        });
    }

    const toggleMapType = function (map) {
        const isStyledMap = map.getMapTypeId() === "styled_map";
        map.setMapTypeId(isStyledMap ? "satellite" : "styled_map");
        setMapStyle(!isStyledMap);
    };

    const toggleEditMode = function () {
        document.body.style.overflow = editMode === true ? "scroll" : "hidden";
        document.body.style.position = editMode === true ? "inherit" : "fixed";
        //document.body.style.top = editMode === true ? "0" : "-142px";
        setEditMode(!editMode);
    }

    const zoomIn = function (map) {
        map.setZoom(map.getZoom() + 1);
    };

    const zoomOut = function (map) {
        map.setZoom(map.getZoom() - 1);
    };

    const addPathPoint = function (e) {
        if (editMode === false) return;
        var p = JSON.parse(JSON.stringify(polygon));
        p.push(e.latLng.toJSON());
        setPolygon(p);
    };

    const updatePathPoint = function (i, e) {
        var p = JSON.parse(JSON.stringify(polygon));
        p[i] = e.latLng.toJSON();
        setPolygon(p);
    };

    const savePathPoint = function (i, e) {
        var p = JSON.parse(JSON.stringify(polygon));
        p[i] = e.latLng.toJSON();
        setPolygon(p);
    };

    const removePathPoint = function (i, e) {
        var p = JSON.parse(JSON.stringify(polygon));
        p.splice(i, 1);
        setPolygon(p);
    };

    const cleanPolygon = function () {
        setPolygon([]);
    }

    const updateMarkerPosition = function (e) {
        setLatLng(e.latLng.toJSON());
    }

    const markerToMapCenter = function (map) {
        setLatLng(map.getCenter().toJSON());
    }

    const markerToPolygonCenter = function () {
        const bounds = new window.google.maps.LatLngBounds();
        polygon.forEach(function (p, i) {
            bounds.extend(p);
        });
        setLatLng(bounds.getCenter().toJSON());
    }

    const setAddress = function (i) {
        var street_number = "";
        var street_name = "";
        const v = JSON.parse(JSON.stringify(value));
        setLatLng(addresses[i].geometry.location);
        setSearch(addresses[i].formatted_address);
        setAddresses([]);
        map.setCenter(addresses[i].geometry.location);
        addresses[i].address_components.forEach(function (c, i) {
            c.types.forEach(function (t, j) {
                switch (t) {
                    case "street_number":
                        street_number = c.long_name;
                        break;
                    case "route":
                        street_name = c.long_name;
                        break;
                    case "locality":
                        v.city = c.long_name;
                        break;
                    case "administrative_area_level_1":
                        v.region = c.long_name;
                        break;
                    case "postal_code":
                        v.postcode = c.long_name;
                        break;
                    case "country":
                        //country = c.long_name;
                        break;
                    default:
                    //nothing
                }
            });
        });
        v.address = street_number + " " + street_name;
        setValue(v);
    }

    const fit = function (map) {
        const bounds = new window.google.maps.LatLngBounds();
        polygon.forEach(function (p, i) {
            bounds.extend(p);
        });
        bounds.extend(latLng);
        map.fitBounds(bounds);
    }

    const goToAddress = function (i) {
        map.setCenter(addresses[i].geometry.location);
    }

    const getStaticMapURL = (project) => {
        var url = "https://maps.googleapis.com/maps/api/staticmap?";
        var params = {
            key: GOOGLE_MAPS_JS_API_KEY,
            size: "600x600",
            maptype: "satellite",
            sensor: "false",
            zoom: project.map_zoom,
            markers: project.latitude + "," + project.longitude,
            path: "weight:2|color:0x00FF00FF|fillcolor:0x00FF0020"
        }
        const p = JSON.parse(project.polygon);
        p.forEach(function (k, i) {
            params.path += "|" + k.lat + "," + k.lng;
        });
        if (p.length > 0) params.path += "|" + p[0].lat + "," + p[0].lng;
        var query = "";
        for (var k in params) {
            query += "&" + k + "=" + params[k];
        }
        url += query.substring(1, query.length);
        return url;
    };

    const onLoad = React.useCallback(function callback(map) {
        if (undefined === map || map === null) return;
        const styledMap = new window.google.maps.StyledMapType(MapStyle);
        map.set("disableDefaultUI", true);
        map.mapTypes.set("styled_map", styledMap);
        map.setMapTypeId("satellite");
        var p = DEFAULT_POLYGON_PARSED;
        var l = DEFAULT_LAT_LNG;
        var v = JSON.parse(JSON.stringify(value));
        if (undefined !== props.value && props.value !== null) {
            l = {
                lat: (Number(props.value.latitude) || DEFAULT_LAT_LNG.lat),
                lng: (Number(props.value.longitude) || DEFAULT_LAT_LNG.lng)
            }
            setLatLng(l);
            if (props.value.polygon) {
                p = JSON.parse(props.value.polygon);
            }
            p = (p || DEFAULT_POLYGON_PARSED);
            setPolygon(p);
            v.latitude = l.lat;
            v.longitude = l.lng;
            v.map_zoom = props.value.map_zoom || MAX_ZOOM;
            setValue(v);
        }
        const bounds = new window.google.maps.LatLngBounds();
        p.forEach(function (k, i) {
            bounds.extend(k);
        });
        bounds.extend(l);
        map.fitBounds(bounds);
        console.log(map)
        setMap(map);
    }, []);


    useEffect(() => {
        if (props.view === true) return;
        var v = JSON.parse(JSON.stringify(value));
        props.onChange({
            target: {
                id: props.id.toString().replace("#", "").split(" ")[0],
                value: v
            },
            currentTarget: {
                id: props.id.toString().replace("#", "").split(" ")[0],
                value: v
            }
        });
    }, [value]);

    useEffect(() => {
        if (props.view === true) {
            var url = getStaticMapURL(props.value);
            setStaticMapURL(url);
            return;
        }
    }, [props.value]);

    useEffect(() => {
        if (props.view === true) return;
        var v = JSON.parse(JSON.stringify(value));
        if (latLng === null) {
            v.latitude = 0;
            v.longitude = 0;
        } else {
            v.latitude = latLng.lat;
            v.longitude = latLng.lng;
        }
        setValue(v);
    }, [latLng]);

    useEffect(() => {
        if (props.view === true) return;
        var v = JSON.parse(JSON.stringify(value));
        v.polygon = JSON.stringify(polygon);
        setValue(v);
    }, [polygon]);

    useEffect(() => {
        return (() => {
            source.cancel();
        });
    });

    if (props.view === true) {
        return (
            <>
                <Image src={staticMapURL} fallback={config.fallback_image}/>
                {props.caption === true && (
                    <p style={{textAlign: "center"}}>
                        {props.value.address},&nbsp;
                        {props.value.city}&nbsp;
                        {props.value.region}&nbsp;
                        {props.value.postcode}&nbsp;
                    </p>
                )}
            </>
        );
    }

    if (props.view !== true) {
        return (
            <>
                <Row>
                    <Col span={21}>
                        <Input
                            type="text"
                            key="search"
                            placeholder={t("Type an Address here.")}
                            value={search}
                            onChange={(e) => updateAddress(e)}
                        />
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Click to search address")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="search"/>}
                                onClick={(e) => searchAddress()}
                            />
                        </Tooltip>
                    </Col>
                </Row>
                {addresses.length > 0 && (
                    <Row>
                        <Col span={24}>
                            <Table
                                key="table"
                                style={{width: "100%"}}
                                dataSource={addresses}
                                columns={columns}
                                pagination={false}
                            />
                        </Col>
                    </Row>)}
                <Row>
                    <Col span={3}>
                        <Tooltip title={t("Toogle Map Type")}>
                            <Button
                                block
                                type="primary"
                                onClick={(e) => toggleMapType(map)}
                                icon={
                                    mapStyle === false ?
                                        <FontAwesomeIcon icon="map"/> :
                                        <FontAwesomeIcon icon={["far", "map"]}/>
                                }
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Zoom In")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="search-plus"/>}
                                onClick={(e) => zoomIn(map)}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Zoom Out")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="search-minus"/>}
                                onClick={(e) => zoomOut(map)}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Fit")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="search-location"/>}
                                onClick={(e) => fit(map)}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Toggle Drawing Mode")}>
                            <Button
                                danger={editMode}
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="pencil-alt"/>}
                                onClick={(e) => toggleEditMode()}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Marker to Polygon Center")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="map-marker-alt"/>}
                                onClick={(e) => markerToPolygonCenter()}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Marker to Map Center")}>
                            <Button
                                block
                                type="primary"
                                icon={<FontAwesomeIcon icon="map-marked-alt"/>}
                                onClick={(e) => markerToMapCenter(map)}
                            />
                        </Tooltip>
                    </Col>
                    <Col span={3}>
                        <Tooltip title={t("Clean Polygon")}>
                            <Button
                                block
                                type="primary"
                                danger
                                icon={<FontAwesomeIcon icon="trash"/>}
                                onClick={(e) => cleanPolygon()}
                            />
                        </Tooltip>
                    </Col>
                </Row>

                {isLoaded && <GoogleMap
                        mapContainerStyle={containerStyle}
                        onLoad={onLoad}
                        googleMapsApiKey={GOOGLE_MAPS_JS_API_KEY}s
                        options={{
                            tilt: 0,
                            heading: 0,
                            disableDoubleClickZoom: true,
                            draggableCursor: (editMode ? "crosshair" : "grab")
                        }}
                        onZoomChanged={() => updateZoomValue()}
                        onClick={(e) => addPathPoint(e)}
                    >
                        {latLng && isNaN(latLng.lat) === false && isNaN(latLng.lng) === false && (
                            <Marker
                                position={latLng}
                                draggable={true}
                                onDragEnd={(e) => updateMarkerPosition(e)}
                            />
                        )}
                        <Polygon
                            options={{
                                fillColor: "#0F0",
                                fillOpacity: 0.6,
                                strokeColor: "#0F0"
                            }}
                            paths={polygon}
                        />
                        {
                            editMode &&
                            (polygon.map((p, i) => {
                                return (
                                    <Circle
                                        key={i}
                                        center={p}
                                        radius={2}
                                        draggable={true}
                                        onDrag={(e) => updatePathPoint(i, e)}
                                        onDragEnd={(e) => savePathPoint(i, e)}
                                        onDblClick={(e) => removePathPoint(i, e)}
                                        options={{
                                            fillOpacity: "0.4",
                                            fillColor: "#F00",
                                            strokeColor: "#F00"
                                        }}
                                    />
                                );
                            }))
                        }
                    </GoogleMap>}
                <Row>
                    <Col>
                        {value.address}&nbsp;
                        {value.suburb},&nbsp;
                        {value.city}&nbsp;
                        {value.region}&nbsp;
                        {value.postcode}
                    </Col>
                </Row>
            </>
        )
    }
}


PolygonMapComponent.getStaticMapURL = (project) => {
    console.log(project)
    var url = "https://maps.googleapis.com/maps/api/staticmap?";
    var params = {
        key: GOOGLE_MAPS_JS_API_KEY,
        size: "600x600",
        maptype: "satellite",
        sensor: "false",
        zoom: project.map_zoom,
        markers: project.latitude + "," + project.longitude,
        path: "weight:2|color:0x00FF00FF|fillcolor:0x00FF0020"
    }
    const p = JSON.parse(project.polygon_project);
    p.forEach(function (k, i) {
        params.path += "|" + k.lat + "," + k.lng;
    });
    if (p.length > 0) params.path += "|" + p[0].lat + "," + p[0].lng;
    var query = "";
    for (var k in params) {
        query += "&" + k + "=" + params[k];
    }
    url += query.substring(1, query.length);
    return url;
};

export default React.memo(PolygonMapComponent)
